import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Repeat } from "lucide-react";
import { adminEventConflicts } from "@/lib/api";
import { WEEKDAYS, fmtRange, fmtTime } from "@/lib/format";

const useDebounced = (value, ms = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

/** Live warning under the room/date fields: what already sits in the calendar where this arrangement wants to be. */
export const EventConflicts = ({ editing }) => {
  const key = useDebounced(JSON.stringify({ date: editing.date, end_date: editing.end_date || null, start_time: editing.start_time || null, end_time: editing.end_time || null, room_id: editing.room_id === "none" ? null : editing.room_id, exclude_event_id: editing.id || null }));
  const probe = JSON.parse(key);
  const enabled = !!probe.date && !!probe.room_id && /^\d{4}-\d{2}-\d{2}$/.test(probe.date);
  const { data, isFetching } = useQuery({ queryKey: ["event-conflicts", key], queryFn: () => adminEventConflicts(probe), enabled, staleTime: 10_000 });
  if (!enabled || !data) return null;
  const { conflicts = [], recurring = [] } = data;
  if (!conflicts.length && !recurring.length) {
    return <p className="flex items-center gap-1.5 text-[12.5px] text-status-confirmed sm:col-span-2" data-testid="event-conflicts-free"><span className="h-1.5 w-1.5 rounded-full bg-status-confirmed" /> Rommet er ledig på denne tida{isFetching ? "…" : "."}</p>;
  }
  return (
    <div className="rounded-xl border border-[hsl(var(--status-pending))] bg-[hsl(var(--status-pending-bg))] px-3.5 py-3 text-[13px] sm:col-span-2" role="status" data-testid="event-conflicts">
      <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 shrink-0 text-status-warning" /> Kolliderer med {conflicts.length + recurring.length === 1 ? "éi oppføring" : `${conflicts.length + recurring.length} oppføringar`} i kalenderen</p>
      <ul className="mt-2 space-y-1 text-[12.5px]">
        {conflicts.map((c) => (
          <li key={c.id} className="flex flex-wrap items-baseline gap-x-2" data-testid={`event-conflict-${c.id}`}>
            <Link to={`/admin/kalender?view=list&omfang=alle&rediger=${c.id}`} className="font-medium u-link">{c.title}</Link>
            <span className="text-muted-foreground">{fmtRange(c.start_date, c.end_date)}{fmtTime(c.start_time, c.end_time) ? ` ${fmtTime(c.start_time, c.end_time)}` : ""} · {c.room_names.join(", ")}{c.source === "booking" ? " · frå booking" : c.event_id ? " · arrangement" : ""}</span>
          </li>
        ))}
        {recurring.map((p) => (
          <li key={p.id} className="flex flex-wrap items-baseline gap-x-2" data-testid={`event-conflict-recurring-${p.id}`}>
            <span className="inline-flex items-center gap-1 font-medium"><Repeat className="h-3 w-3" /> {p.title}</span>
            <span className="text-muted-foreground">fast øving {WEEKDAYS[p.weekday]?.toLowerCase()}ar kl. {p.start_time}–{p.end_time} · {p.room_name}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[12px] text-muted-foreground">Du kan lagre likevel – men sjekk gjerne med dei det gjeld, eller flytt det eine i kalenderen.</p>
    </div>
  );
};
